import {
  type NodeSn,
  type Security,
  isNonNullable,
  isNullable,
} from '@ipfabric/utils';
import { SecuritySchemaModal } from 'components/SecuritySchema/SecuritySchemaModal';
import type { RuleChainScope } from 'components/SecuritySchema/model/SecuritySchemaScope';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';

interface RuleDetailModal {
  highlightedRuleIndex?: number;
  hostSn: NodeSn;
  name: string;
  securityType: Security.SecurityType;
}

export const useTableSecurityModal = (): ReactNode => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as
    | {
        ruleDetailModal: RuleDetailModal;
      }
    | null
    | undefined;
  const snapshotId = useSnapshotId();

  const ruleDetailModal = useMemo(() => {
    if (
      !locationState ||
      !locationState.ruleDetailModal ||
      isNullable(snapshotId)
    ) {
      return null;
    }
    const ruleChainScope: RuleChainScope = {
      name: locationState.ruleDetailModal.name,
      securityType: locationState.ruleDetailModal.securityType,
      type: 'ruleChain',
      ...(locationState.ruleDetailModal.highlightedRuleIndex !== undefined
        ? {
            highlightedRuleIndex:
              locationState.ruleDetailModal.highlightedRuleIndex,
          }
        : {}),
    };

    return {
      ruleChainScope,
      hostIdentifier: {
        hostSn: locationState.ruleDetailModal.hostSn,
        snapshotId,
      },
    };
  }, [snapshotId, locationState]);

  const handleClose = () => {
    navigate(location.pathname, { state: null });
  };

  return isNonNullable(ruleDetailModal?.hostIdentifier) ? (
    <SecuritySchemaModal
      onClose={handleClose}
      hostIdentifier={ruleDetailModal!.hostIdentifier}
      entryPoint={ruleDetailModal!.ruleChainScope}
    />
  ) : null;
};
